/* eslint-disable @typescript-eslint/naming-convention */

import CandidateItem, {
  CandiddateItemCVVideo,
} from '@candidates/models/dto/get-candidates.dto';
import { DialogContentStyled, TableRowStyled } from './candidate-row.styled';
import { Menu, Stack } from '@mui/material';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import {
  useDisableCandidate,
  useEnableCandidate,
} from '@candidates/mutations/candidateMutations';
import { useEffect, useState } from 'react';

import { AxiosResponse } from 'axios';
import CvVideoStatus from '@cv-videos/models/cv-video-status';
import { CvVideoStatusChip } from '@atoms/CvVideoStatusChip';
import { JButton } from '@atoms/JButton';
import JTableCell from '@atoms/JTabelCell';
import { MenuItemStyled } from '@styled/menu-item.styled';
import Paths from '@/routes/paths';
import { ShowMoreButton } from '@atoms/ShowMoreButton';
import { StatusChip } from '@atoms/StatusChip';
import useJDialog from '@/hooks/useJDialog';
import useJSnackbar from '@/hooks/useJSnackbar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Props {
  row: CandidateItem;
}

type enableMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string },
  unknown
>;

type disbaleMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string },
  unknown
>;

const CandidateRow = ({ row }: Props) => {
  const { id, user, deletedAt } = row;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    mutate: enableMutate,
    isSuccess: enableSuccess,
    isError: enableError,
  } = useEnableCandidate();
  const {
    mutate: disableMutate,
    isSuccess: disableSuccess,
    isError: disableError,
  } = useDisableCandidate();
  const [rowMenuanchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const { showJDialog, closeJDialog } = useJDialog();
  const { showSnackbar } = useJSnackbar();
  const queryClient = useQueryClient();
  const open = Boolean(rowMenuanchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRowMenuAnchorEl(event.currentTarget);
  };
  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
  };
  const openDisableDialog = (
    candidateTitle: string,
    candidateId: string,
    disableMutation: disbaleMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('DISABLE_CANDIDATE_TITLE'),
      <DisableDialogContent
        candidateId={candidateId}
        candidateTitle={candidateTitle}
        disableMutation={disableMutation}
      />,
    );
  };
  const openEnableDialog = (
    candidateTitle: string,
    candidateId: string,
    enableMutation: enableMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('ENABLE_CANDIDATE_TITLE'),
      <EnableDialogContent
        candidateId={candidateId}
        candidateTitle={candidateTitle}
        enableMutation={enableMutation}
      />,
    );
  };

  const getCvVideoStatus = (
    candidateCvsStatus: CandiddateItemCVVideo[],
  ): CvVideoStatus | undefined => {
    const accepted = candidateCvsStatus.find(
      (item) => item.status === CvVideoStatus.ACCEPTED,
    );
    const pending = candidateCvsStatus.find(
      (item) => item.status === CvVideoStatus.PENDING,
    );
    const rejected = candidateCvsStatus.find(
      (item) => item.status === CvVideoStatus.REJECTED,
    );
    if (accepted) return CvVideoStatus.ACCEPTED;
    if (pending) return CvVideoStatus.PENDING;
    if (rejected) return CvVideoStatus.REJECTED;
  };

  useEffect(() => {
    if (!enableSuccess) return;
    queryClient.invalidateQueries(['candidates']);

    closeJDialog();
  }, [enableSuccess]);
  useEffect(() => {
    if (!disableSuccess) return;
    queryClient.invalidateQueries(['candidates']);

    closeJDialog();
  }, [disableSuccess]);

  useEffect(() => {
    if (enableError) showSnackbar(t('API_ERROR'), 'error');
  }, [enableError]);
  useEffect(() => {
    if (disableError) showSnackbar(t('API_ERROR'), 'error');
  }, [disableError]);
  const cvVideoStatus = getCvVideoStatus(row.cvVideos);
  return (
    <TableRowStyled key={id} data-test={`candidate-list-row-${id}`}>
      <JTableCell isbold>{user.firstname}</JTableCell>
      <JTableCell isbold>{user.lastname}</JTableCell>
      <JTableCell>{user.email}</JTableCell>
      <JTableCell>{user.mobilePhone}</JTableCell>
      <JTableCell>
        {user.address.city
          ? `${user.address.city.name} - ${user.address.city.zipCode}`
          : user.address.cityName}
      </JTableCell>
      <JTableCell>
        {cvVideoStatus && (
          <CvVideoStatusChip
            label={t(`${cvVideoStatus.toUpperCase()}_CV`)}
            status={cvVideoStatus}
          ></CvVideoStatusChip>
        )}
      </JTableCell>
      <JTableCell>
        <StatusChip
          label={!deletedAt ? t('ACTIVE') : t('INACTIVE')}
          variant={!deletedAt ? 'success' : 'danger'}
        />
      </JTableCell>

      <JTableCell>
        <ShowMoreButton onClick={handleClick} open={open} />
        <Menu
          id="basic-menu"
          anchorEl={rowMenuanchorEl}
          open={open}
          onClose={handleRowMenuClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {!deletedAt && (
            <MenuItemStyled
              red={false}
              green={false}
              data-qa={'navigation'}
              onClick={() =>
                navigate(Paths.CandidateDetails.replace(':id', id))
              }
            >
              Détails de candidat
            </MenuItemStyled>
          )}
          <MenuItemStyled
            red={!deletedAt}
            green={!!deletedAt}
            data-qa={`action-${user.lastname} ${user.firstname}`}
            onClick={() =>
              deletedAt
                ? openEnableDialog(
                    `${row.user.lastname} ${row.user.firstname}`,
                    id,
                    enableMutate,
                  )
                : openDisableDialog(
                    `${row.user.lastname} ${row.user.firstname}`,
                    id,
                    disableMutate,
                  )
            }
          >
            {!deletedAt ? t('DESACTIVATE') : t('ACTIVATE')}
          </MenuItemStyled>
        </Menu>
      </JTableCell>
    </TableRowStyled>
  );
};

export const DisableDialogContent = ({
  candidateTitle,
  candidateId,
  disableMutation,
}: {
  candidateTitle: string;
  candidateId: string;
  disableMutation: disbaleMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>
        {t('DISABLE_CANDIDATE_TEXT')}
        <strong> {candidateTitle}</strong> ?
      </DialogContentStyled>

      <Stack direction={'row'} gap={1}>
        <JButton
          onClick={() => disableMutation({ id: candidateId })}
          variant="contained"
          color="error"
        >
          {t('DISABLE_CANDIDATE_DISABLE_BUTTON')}
        </JButton>
        <JButton onClick={closeJDialog} variant="contained" color="info">
          Annuler
        </JButton>
      </Stack>
    </Stack>
  );
};

const EnableDialogContent = ({
  candidateTitle,
  candidateId,
  enableMutation,
}: {
  candidateTitle: string;
  candidateId: string;
  enableMutation: enableMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>
        {t('ENABLE_CANDIDATE_TEXT')}
        <strong> {candidateTitle}</strong> ?
      </DialogContentStyled>

      <Stack direction={'row'} gap={1}>
        <JButton
          onClick={() => enableMutation({ id: candidateId })}
          variant="contained"
          color="secondary"
        >
          {t('ENABLE_CANDIDATE_DISABLE_BUTTON')}
        </JButton>
        <JButton onClick={closeJDialog} variant="contained" color="info">
          {t('CANCEL')}
        </JButton>
      </Stack>
    </Stack>
  );
};

export default CandidateRow;
